import { Box, Grid, Toolbar, Typography } from "@mui/material";
import { FC, Fragment } from "react";

interface ComponentProps {
  entityTitle?: string;
  entityFields: any[];
  tooltips?: React.ReactNode;
  children?: React.ReactNode;
}

const SingleEntityContainer: FC<ComponentProps> = ({
  entityTitle,
  entityFields,
  tooltips,
  children,
}) => {
  // Separate fields with and without columnPreference
  const fieldsWithPreference = entityFields.filter(
    (field) => field.columnPreference
  );
  const fieldsWithoutPreference = entityFields.filter(
    (field) => !field.columnPreference
  );

  // Sort fields with columnPreference by their preference
  const sortedFieldsWithPreference = fieldsWithPreference.sort(
    (a, b) => a.columnPreference - b.columnPreference
  );

  // Distribute fields without columnPreference evenly between the two columns
  const firstColumnData = [
    ...sortedFieldsWithPreference.filter(
      (field) => field.columnPreference === 1
    ),
  ];
  const secondColumnData = [
    ...sortedFieldsWithPreference.filter(
      (field) => field.columnPreference === 2
    ),
  ];

  const multilineData: any = [];
  fieldsWithoutPreference.forEach((field, index) => {
    if (field.multiline) {
      multilineData.push(field);
    } else {
      if (index % 2 === 0) {
        firstColumnData.push(field); // Add to first column
      } else {
        secondColumnData.push(field); // Add to second column
      }
    }
  });

  return (
    <>
      <Box>
        {entityTitle ||
          (tooltips && (
            <Toolbar sx={{ justifyContent: "space-between", mb: 2 }}>
              <Typography variant="h6">{entityTitle}</Typography>
              <div>{tooltips}</div>
            </Toolbar>
          ))}

        {entityFields.length !== 0 && (
          <Grid container spacing={3} sx={{ px: 3 }}>
            {/* Render first column */}
            <Grid item xs={12} md={6}>
              {firstColumnData.map(
                (dataItem: any, dataIndex: number) =>
                  dataItem?.value && (
                    <Box
                      sx={{
                        mb: 2,
                        display: "block",
                        alignItems: "center",
                        gap: 2, // Adjust spacing here
                        backgroundColor: "#D3E3E030",
                        padding: "24px",
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "#D3E3E070",
                          transition: "background-color 0.3s ease",
                        },
                      }}
                      key={`first-${dataIndex}`}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="500"
                        sx={{
                          flexShrink: 0,
                          width: "35%", // Consistent label width
                          marginBottom: "8px",
                        }}
                      >
                        {dataItem?.label}:
                      </Typography>
                      <Typography variant="body2" flexGrow={1}>
                        {dataItem?.value}
                      </Typography>
                    </Box>
                  )
              )}
            </Grid>

            {/* Render second column */}
            <Grid item xs={12} md={6}>
              {secondColumnData.map(
                (dataItem: any, dataIndex: number) =>
                  dataItem?.value && (
                    <Box
                      key={`second-${dataIndex}`}
                      sx={{
                        mb: 2,
                        display: "block",
                        alignItems: "center",
                        gap: 2, // Adjust spacing here
                        backgroundColor: "#D3E3E030",
                        padding: "24px",
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "#D3E3E070",
                          transition: "background-color 0.3s ease",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="500"
                        sx={{
                          flexShrink: 0,
                          width: "35%", // Consistent label width
                          marginBottom: "8px",
                        }}
                      >
                        {dataItem?.label}:
                      </Typography>
                      <Typography variant="body2" flexGrow={1}>
                        {dataItem?.value}
                      </Typography>
                    </Box>
                  )
              )}
            </Grid>
          </Grid>
        )}

        {/* Render multiline data */}
        {multilineData.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {multilineData.map((dataItem: any, dataIndex: number) => (
              <Fragment key={`multiline-${dataIndex}`}>
                <Box
                  sx={{
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 2, // Adjust spacing here
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{
                      flexShrink: 0,
                      width: "150px", // Consistent label width
                    }}
                  >
                    {dataItem?.label}:
                  </Typography>
                  <Typography variant="body2" flexGrow={1}>
                    {dataItem?.value}
                  </Typography>
                </Box>
              </Fragment>
            ))}
          </Box>
        )}
      </Box>
      <Box sx={{ p: 2.5 }}>{children}</Box>
    </>
  );
};

export default SingleEntityContainer;
